/**
 * ElementInspector - Right panel controls for selected element
 * 
 * Tabbed interface with:
 * - Content tab: Text/image/button value editing
 * - Style tab: Safe style controls (size, weight, opacity, etc.)
 * - Position tab: Guided positioning controls (align, nudge, spacing)
 * - Motion tab: Per-element animation controls
 */
import React, { useState, useCallback, useMemo } from 'react';
import {
  Type, Image, MousePointer2, Palette, Sparkles, Play,
  AlignLeft, AlignCenter, AlignRight, X, Upload, Move
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Slider } from '../ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  ELEMENT_TYPES,
  STYLE_PRESETS,
  MOTION_PRESETS,
  getElementValue,
  getElementStyles,
  getElementMotion,
} from './ElementRegistry';
import PositionInspector from './PositionInspector';

// Tab icons
const TAB_ICONS = {
  content: Type,
  style: Palette,
  position: Move,
  motion: Sparkles,
};

// Text size to pixel mapping for display
const TEXT_SIZE_MAP = {
  'xs': '12px',
  'sm': '14px',
  'base': '16px',
  'lg': '18px',
  'xl': '20px',
  '2xl': '24px',
  '3xl': '30px',
  '4xl': '36px',
};

const ElementInspector = ({
  element,
  blockProps,
  onUpdateValue,
  onUpdateStyles,
  onUpdatePosition,
  onUpdateMotion,
  onReplayAnimation,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState('content');
  
  // Get current values
  const currentValue = getElementValue(blockProps, element);
  const currentStyles = getElementStyles(blockProps, element);
  const currentMotion = getElementMotion(blockProps, element);
  
  // Get position from styles or default
  const currentPosition = currentStyles?.position || {};

  // Content update handlers
  const handleTextChange = useCallback((newText) => {
    onUpdateValue(newText);
  }, [onUpdateValue]);

  const handleButtonLabelChange = useCallback((newLabel) => {
    const current = typeof currentValue === 'object' ? currentValue : { label: '', destination: '/' };
    onUpdateValue({ ...current, label: newLabel });
  }, [currentValue, onUpdateValue]);

  const handleButtonDestChange = useCallback((newDest) => {
    const current = typeof currentValue === 'object' ? currentValue : { label: '', destination: '/' };
    onUpdateValue({ ...current, destination: newDest });
  }, [currentValue, onUpdateValue]);

  const handleImageUrlChange = useCallback((newUrl) => {
    onUpdateValue(newUrl);
  }, [onUpdateValue]);

  // Style update handlers
  const handleStyleChange = useCallback((key, value) => {
    onUpdateStyles({ ...currentStyles, [key]: value });
  }, [currentStyles, onUpdateStyles]);

  // Position update handler
  const handlePositionChange = useCallback((newPosition) => {
    // Position is stored within styles as a nested object
    onUpdateStyles({ ...currentStyles, position: newPosition });
    // Also call dedicated position handler if provided
    onUpdatePosition?.(newPosition);
  }, [currentStyles, onUpdateStyles, onUpdatePosition]);

  // Motion update handlers
  const handleMotionChange = useCallback((key, value) => {
    onUpdateMotion({ ...currentMotion, [key]: value });
  }, [currentMotion, onUpdateMotion]);

  // Render content tab based on element type
  const renderContentTab = () => {
    switch (element.type) {
      case ELEMENT_TYPES.TEXT:
      case ELEMENT_TYPES.BADGE:
        // Handle object values for cards/items (e.g., {label, description, icon} or {title, description, icon})
        if (typeof currentValue === 'object' && currentValue !== null) {
          const objValue = currentValue;
          const hasLabel = 'label' in objValue;
          const hasTitle = 'title' in objValue;
          const mainKey = hasLabel ? 'label' : hasTitle ? 'title' : null;
          
          return (
            <div className="space-y-4">
              {/* Main text field (label or title) */}
              {mainKey && (
                <div>
                  <Label className="text-zinc-300 text-sm mb-2 block capitalize">{mainKey}</Label>
                  <Input
                    value={objValue[mainKey] || ''}
                    onChange={(e) => onUpdateValue({ ...objValue, [mainKey]: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    placeholder={`Enter ${mainKey}...`}
                  />
                </div>
              )}
              
              {/* Description field */}
              {'description' in objValue && (
                <div>
                  <Label className="text-zinc-300 text-sm mb-2 block">Description</Label>
                  <Textarea
                    value={objValue.description || ''}
                    onChange={(e) => onUpdateValue({ ...objValue, description: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white min-h-[80px] resize-none"
                    placeholder="Enter description..."
                  />
                </div>
              )}
              
              {/* Icon field */}
              {'icon' in objValue && (
                <div>
                  <Label className="text-zinc-300 text-sm mb-2 block">Icon</Label>
                  <Select
                    value={objValue.icon || 'BookOpen'}
                    onValueChange={(value) => onUpdateValue({ ...objValue, icon: value })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {['BookOpen', 'Pen', 'Palette', 'Eye', 'Shield', 'Zap', 'Users', 'Upload'].map(icon => (
                        <SelectItem key={icon} value={icon} className="text-white">
                          {icon}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          );
        }
        
        // Handle string values (default case)
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-300 text-sm mb-2 block">Text Content</Label>
              <Textarea
                value={currentValue || ''}
                onChange={(e) => handleTextChange(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white min-h-[100px] resize-none"
                placeholder="Enter text..."
              />
              {element.constraints?.maxLength && (
                <p className="text-xs text-zinc-500 mt-1">
                  {(currentValue || '').length} / {element.constraints.maxLength} characters
                </p>
              )}
            </div>
          </div>
        );

      case ELEMENT_TYPES.BUTTON:
        const buttonValue = typeof currentValue === 'object' 
          ? currentValue 
          : { label: currentValue || '', destination: '/' };
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-300 text-sm mb-2 block">Button Label</Label>
              <Input
                value={buttonValue.label || ''}
                onChange={(e) => handleButtonLabelChange(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white"
                placeholder="Button text..."
              />
            </div>
            <div>
              <Label className="text-zinc-300 text-sm mb-2 block">Destination URL</Label>
              <Input
                value={buttonValue.destination || ''}
                onChange={(e) => handleButtonDestChange(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white"
                placeholder="/path or https://..."
              />
            </div>
          </div>
        );

      case ELEMENT_TYPES.IMAGE:
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-300 text-sm mb-2 block">Image URL</Label>
              <Input
                value={currentValue || ''}
                onChange={(e) => handleImageUrlChange(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white"
                placeholder="/path/to/image.png"
              />
            </div>
            {currentValue && (
              <div className="mt-2">
                <Label className="text-zinc-300 text-sm mb-2 block">Preview</Label>
                <div className="bg-zinc-800 rounded-lg p-4 flex items-center justify-center">
                  <img 
                    src={currentValue} 
                    alt="Preview" 
                    className="max-h-24 w-auto object-contain"
                    onError={(e) => { e.target.src = '/logo.svg'; }}
                  />
                </div>
              </div>
            )}
            <Button variant="outline" className="w-full border-zinc-700 text-zinc-300">
              <Upload className="w-4 h-4 mr-2" />
              Upload Image
            </Button>
          </div>
        );

      default:
        return (
          <div className="text-zinc-500 text-sm">
            No content controls for this element type.
          </div>
        );
    }
  };

  // Render style tab based on element type
  const renderStyleTab = () => {
    switch (element.type) {
      case ELEMENT_TYPES.TEXT:
      case ELEMENT_TYPES.BADGE:
        return (
          <div className="space-y-5">
            {/* Text Size */}
            <div>
              <div className="flex justify-between mb-2">
                <Label className="text-zinc-300 text-sm">Size</Label>
                <span className="text-xs text-zinc-500 font-mono">
                  {TEXT_SIZE_MAP[currentStyles.size] || currentStyles.size}
                </span>
              </div>
              <Select
                value={currentStyles.size || 'base'}
                onValueChange={(value) => handleStyleChange('size', value)}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {STYLE_PRESETS.textSizes.map(size => (
                    <SelectItem key={size} value={size} className="text-white">
                      {size} ({TEXT_SIZE_MAP[size]})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Font Weight */}
            <div>
              <Label className="text-zinc-300 text-sm mb-2 block">Weight</Label>
              <Select
                value={currentStyles.weight || 'normal'}
                onValueChange={(value) => handleStyleChange('weight', value)}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {STYLE_PRESETS.fontWeights.map(weight => (
                    <SelectItem key={weight} value={weight} className="text-white capitalize">
                      {weight}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Letter Spacing */}
            <div>
              <div className="flex justify-between mb-2">
                <Label className="text-zinc-300 text-sm">Letter Spacing</Label>
                <span className="text-xs text-zinc-500 font-mono">
                  {currentStyles.letterSpacing || 0}px
                </span>
              </div>
              <Slider
                value={[currentStyles.letterSpacing || 0]}
                onValueChange={([value]) => handleStyleChange('letterSpacing', value)}
                min={-2}
                max={10}
                step={0.5}
                className="w-full"
              />
            </div>

            {/* Opacity */}
            <div>
              <div className="flex justify-between mb-2">
                <Label className="text-zinc-300 text-sm">Opacity</Label>
                <span className="text-xs text-zinc-500 font-mono">
                  {Math.round((currentStyles.opacity ?? 1) * 100)}%
                </span>
              </div>
              <Slider
                value={[(currentStyles.opacity ?? 1) * 100]}
                onValueChange={([value]) => handleStyleChange('opacity', value / 100)}
                min={10}
                max={100}
                step={5}
                className="w-full"
              />
            </div>

            {/* Alignment */}
            <div>
              <Label className="text-zinc-300 text-sm mb-2 block">Alignment</Label>
              <div className="flex gap-1 bg-zinc-800 rounded-lg p-1">
                {[
                  { value: 'left', icon: AlignLeft },
                  { value: 'center', icon: AlignCenter },
                  { value: 'right', icon: AlignRight },
                ].map(({ value, icon: Icon }) => (
                  <button
                    key={value}
                    onClick={() => handleStyleChange('alignment', value)}
                    className={`flex-1 p-2 rounded transition-colors ${
                      currentStyles.alignment === value
                        ? 'bg-orange-500 text-white'
                        : 'text-zinc-400 hover:text-white hover:bg-zinc-700'
                    }`}
                  >
                    <Icon className="w-4 h-4 mx-auto" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case ELEMENT_TYPES.IMAGE:
        return (
          <div className="space-y-5">
            {/* Scale */}
            <div>
              <div className="flex justify-between mb-2">
                <Label className="text-zinc-300 text-sm">Scale</Label>
                <span className="text-xs text-zinc-500 font-mono">
                  {Math.round((currentStyles.scale || 1) * 100)}%
                </span>
              </div>
              <Slider
                value={[(currentStyles.scale || 1) * 100]}
                onValueChange={([value]) => handleStyleChange('scale', value / 100)}
                min={50}
                max={150}
                step={5}
                className="w-full"
              />
            </div>

            {/* Opacity */}
            <div>
              <div className="flex justify-between mb-2">
                <Label className="text-zinc-300 text-sm">Opacity</Label>
                <span className="text-xs text-zinc-500 font-mono">
                  {Math.round((currentStyles.opacity ?? 1) * 100)}%
                </span>
              </div>
              <Slider
                value={[(currentStyles.opacity ?? 1) * 100]}
                onValueChange={([value]) => handleStyleChange('opacity', value / 100)}
                min={10}
                max={100}
                step={5}
                className="w-full"
              />
            </div>

            {/* Glow Strength */}
            <div>
              <div className="flex justify-between mb-2">
                <Label className="text-zinc-300 text-sm">Glow Effect</Label>
                <span className="text-xs text-zinc-500 font-mono">
                  {currentStyles.glowStrength || 0}
                </span>
              </div>
              <Slider
                value={[currentStyles.glowStrength || 0]}
                onValueChange={([value]) => handleStyleChange('glowStrength', value)}
                min={0}
                max={20}
                step={1}
                className="w-full"
              />
            </div>
          </div>
        );

      case ELEMENT_TYPES.BUTTON:
        return (
          <div className="space-y-5">
            {/* Variant */}
            <div>
              <Label className="text-zinc-300 text-sm mb-2 block">Variant</Label>
              <Select
                value={currentStyles.variant || 'primary'}
                onValueChange={(value) => handleStyleChange('variant', value)}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {STYLE_PRESETS.buttonVariants.map(variant => (
                    <SelectItem key={variant} value={variant} className="text-white capitalize">
                      {variant}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Padding */}
            <div>
              <Label className="text-zinc-300 text-sm mb-2 block">Padding</Label>
              <Select
                value={currentStyles.padding || 'md'}
                onValueChange={(value) => handleStyleChange('padding', value)}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {STYLE_PRESETS.paddingSizes.map(size => (
                    <SelectItem key={size} value={size} className="text-white uppercase">
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Border Radius */}
            <div>
              <Label className="text-zinc-300 text-sm mb-2 block">Corner Radius</Label>
              <Select
                value={currentStyles.radius || 'md'}
                onValueChange={(value) => handleStyleChange('radius', value)}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {STYLE_PRESETS.radiusSizes.map(size => (
                    <SelectItem key={size} value={size} className="text-white capitalize">
                      {size === 'none' ? 'Square' : size === 'full' ? 'Pill' : size.toUpperCase()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      default:
        return (
          <div className="text-zinc-500 text-sm">
            No style controls for this element type.
          </div>
        );
    }
  };

  // Render motion tab
  const renderMotionTab = () => {
    return (
      <div className="space-y-5">
        {/* Replay Button */}
        <Button
          variant="outline"
          onClick={onReplayAnimation}
          className="w-full border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
        >
          <Play className="w-4 h-4 mr-2" />
          Replay Animation
        </Button>

        {/* Duration */}
        <div>
          <div className="flex justify-between mb-2">
            <Label className="text-zinc-300 text-sm">Duration</Label>
            <span className="text-xs text-zinc-500 font-mono">
              {currentMotion.durationMs || 700}ms
            </span>
          </div>
          <Slider
            value={[currentMotion.durationMs || 700]}
            onValueChange={([value]) => handleMotionChange('durationMs', value)}
            min={MOTION_PRESETS.durations.min}
            max={MOTION_PRESETS.durations.max}
            step={MOTION_PRESETS.durations.step}
            className="w-full"
          />
        </div>

        {/* Delay */}
        <div>
          <div className="flex justify-between mb-2">
            <Label className="text-zinc-300 text-sm">Delay</Label>
            <span className="text-xs text-zinc-500 font-mono">
              {currentMotion.delayMs || 0}ms
            </span>
          </div>
          <Slider
            value={[currentMotion.delayMs || 0]}
            onValueChange={([value]) => handleMotionChange('delayMs', value)}
            min={MOTION_PRESETS.delays.min}
            max={MOTION_PRESETS.delays.max}
            step={MOTION_PRESETS.delays.step}
            className="w-full"
          />
        </div>

        {/* Easing */}
        <div>
          <Label className="text-zinc-300 text-sm mb-2 block">Easing</Label>
          <Select
            value={currentMotion.easing || 'smooth'}
            onValueChange={(value) => handleMotionChange('easing', value)}
          >
            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              {MOTION_PRESETS.easings.map(easing => (
                <SelectItem key={easing} value={easing} className="text-white capitalize">
                  {easing}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="border-t border-zinc-800 pt-4 mt-4">
          <p className="text-xs text-zinc-500 mb-3">Entrance Animation</p>
          
          {/* From Opacity */}
          <div className="mb-4">
            <div className="flex justify-between mb-2">
              <Label className="text-zinc-400 text-xs">Start Opacity</Label>
              <span className="text-xs text-zinc-500 font-mono">
                {Math.round((currentMotion.fromOpacity ?? 0) * 100)}%
              </span>
            </div>
            <Slider
              value={[(currentMotion.fromOpacity ?? 0) * 100]}
              onValueChange={([value]) => handleMotionChange('fromOpacity', value / 100)}
              min={0}
              max={100}
              step={10}
              className="w-full"
            />
          </div>

          {/* From Y */}
          <div className="mb-4">
            <div className="flex justify-between mb-2">
              <Label className="text-zinc-400 text-xs">Start Y Offset</Label>
              <span className="text-xs text-zinc-500 font-mono">
                {currentMotion.fromY || 0}px
              </span>
            </div>
            <Slider
              value={[currentMotion.fromY || 0]}
              onValueChange={([value]) => handleMotionChange('fromY', value)}
              min={MOTION_PRESETS.fromY.min}
              max={MOTION_PRESETS.fromY.max}
              step={MOTION_PRESETS.fromY.step}
              className="w-full"
            />
          </div>

          {/* From Scale */}
          <div>
            <div className="flex justify-between mb-2">
              <Label className="text-zinc-400 text-xs">Start Scale</Label>
              <span className="text-xs text-zinc-500 font-mono">
                {Math.round((currentMotion.fromScale ?? 1) * 100)}%
              </span>
            </div>
            <Slider
              value={[(currentMotion.fromScale ?? 1) * 100]}
              onValueChange={([value]) => handleMotionChange('fromScale', value / 100)}
              min={MOTION_PRESETS.fromScale.min * 100}
              max={MOTION_PRESETS.fromScale.max * 100}
              step={1}
              className="w-full"
            />
          </div>
        </div>
      </div>
    );
  };

  // Get type icon component
  const TypeIcon = useMemo(() => {
    switch (element.type) {
      case ELEMENT_TYPES.TEXT:
      case ELEMENT_TYPES.BADGE:
        return Type;
      case ELEMENT_TYPES.IMAGE:
        return Image;
      case ELEMENT_TYPES.BUTTON:
        return MousePointer2;
      default:
        return Type;
    }
  }, [element.type]);

  return (
    <div className="h-full flex flex-col" data-testid="element-inspector">
      {/* Header */}
      <div className="p-4 border-b border-zinc-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded bg-orange-500/20">
              <TypeIcon className="w-4 h-4 text-orange-400" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-white">{element.label}</h3>
              <p className="text-xs text-zinc-500 capitalize">{element.type} element</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-zinc-800 rounded text-zinc-400 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <TabsList className="grid grid-cols-4 m-3 bg-zinc-800/50">
          {Object.entries(TAB_ICONS).map(([key, Icon]) => (
            <TabsTrigger
              key={key}
              value={key}
              className="data-[state=active]:bg-orange-500 data-[state=active]:text-white text-xs px-2"
            >
              <Icon className="w-3.5 h-3.5 mr-1" />
              <span className="hidden sm:inline">{key.charAt(0).toUpperCase() + key.slice(1)}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        <div className="flex-1 overflow-y-auto px-4 pb-4">
          <TabsContent value="content" className="mt-0">
            {renderContentTab()}
          </TabsContent>
          <TabsContent value="style" className="mt-0">
            {renderStyleTab()}
          </TabsContent>
          <TabsContent value="position" className="mt-0">
            <PositionInspector
              position={currentPosition}
              onUpdatePosition={handlePositionChange}
              elementLabel={element.label}
            />
          </TabsContent>
          <TabsContent value="motion" className="mt-0">
            {renderMotionTab()}
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
};

export default ElementInspector;
